import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { api } from '../utils/api';
import { Mail, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * AUTH-02 — Amber banner shown at the top of the dashboard whenever
 * `user.email_verified === false`. Per spec:
 *   - Resend button respects a 60s cooldown
 *   - Dismiss is per-session only (not persisted) — reappears on reload
 *   - Banner is silent for legacy users (`email_verified` absent/true)
 */
const maskEmail = (email) => {
  if (!email || !email.includes('@')) return email || '';
  const [local, domain] = email.split('@');
  if (local.length <= 1) return `${local}*@${domain}`;
  return `${local[0]}${'*'.repeat(Math.max(1, local.length - 1))}@${domain}`;
};

export const EmailVerificationBanner = () => {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const [dismissed, setDismissed] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [sending, setSending] = useState(false);

  // Tick the cooldown counter every second
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const id = setInterval(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  // Bail out: legacy users (absent field) and verified users see nothing.
  if (!user || user.email_verified !== false || dismissed) return null;

  const handleResend = async () => {
    if (cooldown > 0 || sending) return;
    setSending(true);
    try {
      const res = await api.resendVerification();
      toast.success(res?.message || 'Email de verificação enviado.');
      setCooldown(Number(res?.cooldown_seconds) || 60);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 429 && typeof detail === 'object') {
        const remaining = Number(detail?.retry_after_seconds) || 60;
        setCooldown(remaining);
        toast.error(detail?.message || `Aguarde ${remaining}s antes de reenviar.`);
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Não foi possível reenviar agora. Tente novamente em instantes.');
      }
    } finally {
      setSending(false);
    }
  };

  const bg = isDark
    ? 'bg-amber-950/40 border-amber-900/60 text-amber-100'
    : 'bg-amber-50 border-amber-200 text-amber-900';

  return (
    <div
      className={`mb-6 flex items-start gap-3 p-4 rounded-lg border ${bg}`}
      data-testid="email-verification-banner"
      role="status"
    >
      <Mail className="w-5 h-5 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-sm leading-snug">
          <strong>Confirme seu email</strong> para desbloquear a geração de prontuários.
          Enviamos um link para <span className="font-mono" data-testid="banner-masked-email">{maskEmail(user.email)}</span>.
        </p>
        <button
          type="button"
          onClick={handleResend}
          disabled={cooldown > 0 || sending}
          className={`mt-2 text-sm underline underline-offset-2 font-medium transition-opacity ${
            cooldown > 0 || sending ? 'opacity-60 cursor-not-allowed' : 'hover:opacity-80'
          }`}
          data-testid="banner-resend-btn"
        >
          {sending && <Loader2 className="inline w-3.5 h-3.5 mr-1 animate-spin" />}
          {cooldown > 0 ? `Aguarde ${cooldown}s...` : 'Reenviar email'}
        </button>
      </div>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        className="shrink-0 p-1 hover:opacity-70"
        aria-label="Dispensar"
        data-testid="banner-dismiss-btn"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default EmailVerificationBanner;
